"use client";
/**
 * BillMOReviewPanel
 *
 * Side-by-side review drawer: bill PDF (left) + MO details (right).
 * Triggered by "View MO" button from Bills3TablePage or InvoiceUploadView3.
 * Works for both pending and processed bills.
 *
 * Props:
 *   bill         — the full bill object (populated relatedMO3, attachments, etc.)
 *   projectId    — string project ID (for navigation links)
 *   onClose      — () => void
 *   onConfirm    — optional (billId) => void  — confirm button (pending only)
 *   onSave       — optional (billId, patch) => void — inline field edits
 *
 * Usage in Bills3TablePage — add a "Review" button per row that calls:
 *   setReviewBill(bill)
 * Then render:
 *   {reviewBill && (
 *     <BillMOReviewPanel
 *       bill={reviewBill}
 *       projectId={reviewBill.project?._id || reviewBill.project}
 *       onClose={() => setReviewBill(null)}
 *       onConfirm={confirmBill}
 *     />
 *   )}
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTabs } from "./TabContext";
const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isCoverageBased = (uom = "") => ["sf", "lf"].includes(uom.toLowerCase());

// ── Status pill ───────────────────────────────────────────────────────────────
const StatusPill = ({ status, map }) => {
  const cfg = map[status] || "bg-gray-700 text-gray-300";
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg}`}
    >
      {status?.toUpperCase() || "—"}
    </span>
  );
};

const MO_STATUS = {
  pending: "bg-yellow-900/60 text-yellow-200",
  confirmed: "bg-blue-900/60 text-blue-200",
  partial: "bg-orange-900/60 text-orange-200",
  delivered: "bg-emerald-900/60 text-emerald-200",
  void: "bg-red-900/60 text-red-200",
};

const INV_STATUS = {
  paid: "bg-emerald-900/60 text-emerald-200",
  unpaid: "bg-red-900/60 text-red-200",
  partial: "bg-amber-900/60 text-amber-200",
};

// ── Comparison row — highlights mismatches ───────────────────────────────────
const CmpRow = ({ label, billVal, moVal, money = false, warn = false }) => {
  const bStr = money && billVal != null ? `$${fmt(billVal)}` : (billVal ?? "—");
  const mStr = money && moVal != null ? `$${fmt(moVal)}` : (moVal ?? "—");

  // Numeric mismatch check
  let mismatch = false;
  if (money && billVal != null && moVal != null) {
    mismatch = Math.abs(parseFloat(billVal) - parseFloat(moVal)) > 0.5;
  } else if (!money && billVal && moVal) {
    mismatch =
      String(billVal).trim().toLowerCase() !==
      String(moVal).trim().toLowerCase();
  }
  mismatch = mismatch || warn;

  return (
    <div
      className={`grid grid-cols-[130px_1fr_1fr] gap-2 py-1.5 px-2 rounded text-xs ${mismatch ? "bg-red-950/30 border border-red-800/40" : "odd:bg-gray-800/30"}`}
    >
      <span className="text-gray-500 font-medium">{label}</span>
      <span
        className={mismatch ? "text-red-300 font-semibold" : "text-gray-200"}
      >
        {bStr}
      </span>
      <span
        className={mismatch ? "text-red-300 font-semibold" : "text-gray-200"}
      >
        {mStr}
      </span>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function BillMOReviewPanel({
  bill,
  projectId,
  onClose,
  onConfirm,
  onSave,
}) {
  const [mo, setMo] = useState(null);
  const [moLoading, setMoLoading] = useState(false);
  const [moError, setMoError] = useState(null);
  const { openTab } = useTabs();
  // PDF viewer state
  const [activePdf, setActivePdf] = useState(null); // url or null
  const [pdfIndex, setPdfIndex] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);

  // Panel width split (drag handle)
  const [splitPct, setSplitPct] = useState(50);
  const [dragging, setDragging] = useState(false);

  // Comparison mode toggle
  const [showComparison, setShowComparison] = useState(true);

  // ── Load full MO ─────────────────────────────────────────────────────────
  useEffect(() => {
    const moId = bill?.relatedMO3?._id || bill?.relatedMO3;
    if (!moId) return;
    setMoLoading(true);
    setMoError(null);
    axios
      .get(`${API}/materialOrders3/${moId}`, { headers: authH() })
      .then((r) => setMo(r.data.order || r.data))
      .catch((e) => setMoError(e.response?.data?.message || e.message))
      .finally(() => setMoLoading(false));
  }, [bill]);

  // ── Auto-select first PDF attachment ─────────────────────────────────────
  useEffect(() => {
    const atts = bill?.attachments || [];
    if (atts.length > 0) {
      setActivePdf(atts[0].url);
      setPdfIndex(0);
    } else {
      setActivePdf(null);
    }
  }, [bill]);

  // ── Drag-to-resize ────────────────────────────────────────────────────────
  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging) return;
      const panel = document.getElementById("brp-container");
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.max(25, Math.min(75, pct)));
    },
    [dragging],
  );

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", () => setDragging(false));
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", () => setDragging(false));
    };
  }, [dragging, handleMouseMove]);

  // ── Derived helpers ───────────────────────────────────────────────────────
  const attachments = bill?.attachments || [];
  const moItems = mo?.items || [];
  const billVendor =
    bill?.payTo?.companyName || bill?.subContractor?.displayName || "—";
  const moVendor = mo
    ? (typeof mo.vendor === "object" ? mo.vendor?.companyName : null) || "—"
    : "—";
  const moTotal =
    (mo?.items || []).reduce((s, i) => s + (i.total || 0), 0) +
    (mo?.surcharges || []).reduce(
      (s, sc) => s + parseFloat(sc.price || sc.amount || 0),
      0,
    );
  const creditTotal = (bill?.credits || []).reduce(
    (s, c) => s + (parseFloat(c.amount) || 0),
    0,
  );
  const effectiveBillAmount = parseFloat(bill?.subtotal ?? 0) - creditTotal;

  const billPoNum = bill?.relatedPO3?.poNumber || "—";
  const moPoNum = mo?.purchaseOrder?.poNumber || "—";

  // ── Keyboard close ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Add this just before the comparison panel JSX, alongside the other derived values:
  const subtotalMatchesMO = mo
    ? Math.abs(effectiveBillAmount - moTotal) <= 0.5
    : true;
  const moDelivered = mo ? mo.status === "delivered" : true;
  const vendorMatch = billVendor === moVendor;
  const poMatch = billPoNum === moPoNum;
  const hasMismatch =
    !subtotalMatchesMO || !moDelivered || !vendorMatch || !poMatch;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/80 backdrop-blur-sm">
      {/* ── Top bar ── */}
      <div className="flex-shrink-0 flex items-center justify-between bg-[#0d1117] border-b border-gray-800 px-4 py-2.5 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            title="Close (Esc)"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-white truncate max-w-[220px]">
              {bill?.billName || bill?.invoiceNumber || "Bill Review"}
            </span>
            <StatusPill status={bill?.invoiceStatus} map={INV_STATUS} />
            <StatusPill
              status={bill?.processingStatus}
              map={{
                pending: "bg-yellow-900/60 text-yellow-200",
                processed: "bg-emerald-900/60 text-emerald-200",
              }}
            />
          </div>

          {mo && (
            <>
              <span className="text-gray-700 text-xs">vs</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-orange-300">
                  MO #{mo.orderNumber}
                </span>
                <StatusPill status={mo.status} map={MO_STATUS} />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Comparison toggle */}
          <button
            onClick={() => setShowComparison((p) => !p)}
            className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
              showComparison
                ? "bg-indigo-600 border-indigo-500 text-white"
                : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500"
            }`}
          >
            {showComparison ? "⇄ Comparison On" : "⇄ Comparison Off"}
          </button>
          {/* Open MO in new tab */}
          {/* Open MO in the tab workspace */}
          {mo && (projectId || mo.project) && (
            <button
              type="button"
              onClick={() => {
                const pid = projectId || mo.project?._id || mo.project;
                openTab(`/projects/${pid}/material-orders3/${mo._id}`);
                onClose();
              }}
              className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              ↗ Open MO
            </button>
          )}
          {/* Confirm bill (pending only) */}

          {bill?.processingStatus === "pending" &&
            onConfirm &&
            (() => {
              // Use the outer creditTotal / effectiveBillAmount already computed above
              const canConfirm = subtotalMatchesMO && moDelivered;

              return (
                <button
                  onClick={() => {
                    if (!canConfirm) return;
                    onConfirm(bill._id);
                    onClose();
                  }}
                  disabled={!canConfirm}
                  title={
                    !moDelivered
                      ? "MO must be delivered before confirming"
                      : !subtotalMatchesMO
                        ? `Effective bill amount $${fmt(effectiveBillAmount)} doesn't match MO total $${fmt(moTotal)}`
                        : ""
                  }
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg text-white transition-colors ${
                    canConfirm
                      ? "bg-emerald-600 hover:bg-emerald-500"
                      : "bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600"
                  }`}
                >
                  {canConfirm
                    ? "✓ Confirm Bill"
                    : !moDelivered
                      ? "⚠ MO Not Delivered"
                      : "⚠ Amount Mismatch"}
                </button>
              );
            })()}
        </div>
      </div>

      {/* ── Main split pane ── */}
      <div
        id="brp-container"
        className="flex-1 flex overflow-hidden select-none"
      >
        {/* ═══ LEFT: PDF viewer ═══ */}
        <div
          className="flex flex-col bg-[#111827] border-r border-gray-800 overflow-hidden"
          style={{ width: `${splitPct}%` }}
        >
          {/* PDF toolbar */}
          <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-[#0d1117] border-b border-gray-800">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">
              Invoice PDF
            </span>

            {attachments.length > 1 && (
              <div className="flex items-center gap-1">
                {attachments.map((a, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setActivePdf(a.url);
                      setPdfIndex(i);
                    }}
                    className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                      i === pdfIndex
                        ? "bg-orange-500 text-white"
                        : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                    title={a.name}
                  >
                    {a.name?.split(".")[0]?.slice(0, 12) || `File ${i + 1}`}
                  </button>
                ))}
              </div>
            )}

            {attachments.length === 1 && (
              <span className="text-[11px] text-gray-500 truncate max-w-[160px]">
                {attachments[0].name}
              </span>
            )}

            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={() => setPdfScale((s) => Math.max(0.5, s - 0.25))}
                className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs transition-colors"
                title="Zoom out"
              >
                −
              </button>
              <span className="text-[10px] text-gray-500 w-10 text-center">
                {Math.round(pdfScale * 100)}%
              </span>
              <button
                onClick={() => setPdfScale((s) => Math.min(3, s + 0.25))}
                className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs transition-colors"
                title="Zoom in"
              >
                +
              </button>
              <button
                onClick={() => setPdfScale(1)}
                className="px-1.5 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-400 text-[10px] transition-colors ml-1"
                title="Reset zoom"
              >
                ↺
              </button>
              {activePdf && (
                <a
                  href={activePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 px-2 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-blue-400 text-[10px] transition-colors"
                >
                  ↗ Open
                </a>
              )}
            </div>
          </div>

          {/* PDF frame */}
          <div className="flex-1 overflow-auto bg-gray-950 flex items-start justify-center p-4">
            {attachments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-600">
                <svg
                  className="w-12 h-12 opacity-40"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-sm">No invoice PDF attached</p>
                <p className="text-xs opacity-60">
                  Attach a file to this bill to see it here
                </p>
              </div>
            ) : activePdf ? (
              <div
                className="rounded-lg overflow-hidden shadow-2xl"
                style={{
                  width: `${100 * pdfScale}%`,
                  maxWidth: pdfScale > 1 ? "none" : "100%",
                  minWidth: "200px",
                  transition: "width 0.15s ease",
                }}
              >
                {activePdf.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img
                    src={activePdf}
                    alt="Invoice"
                    className="w-full h-auto block"
                  />
                ) : (
                  <iframe
                    src={activePdf + "#toolbar=1&navpanes=0"}
                    title="Invoice PDF"
                    className="w-full border-0 bg-white"
                    style={{
                      height: `calc(${100 / pdfScale}vh - 120px)`,
                      minHeight: "600px",
                    }}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* ═══ Drag handle ═══ */}
        <div
          className="w-1.5 cursor-col-resize bg-gray-800 hover:bg-orange-500/60 active:bg-orange-500 transition-colors flex-shrink-0 relative group"
          onMouseDown={() => setDragging(true)}
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-gray-600 group-hover:bg-orange-400 transition-colors" />
        </div>

        {/* ═══ RIGHT: MO details ═══ */}
        <div
          className="flex flex-col bg-[#0d1117] overflow-hidden"
          style={{ width: `${100 - splitPct}%` }}
        >
          {/* MO header tab bar */}
          <div className="flex-shrink-0 flex items-center gap-0 px-4 py-2 bg-[#0d1117] border-b border-gray-800">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Material Order
            </span>
            {mo && (
              <span className="ml-2 text-xs text-orange-400 font-mono">
                {mo.orderNumber} ·{" "}
                {(typeof mo.vendor === "object"
                  ? mo.vendor?.companyName
                  : null) || ""}
              </span>
            )}
          </div>

          {moLoading && (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <svg
                className="w-5 h-5 animate-spin mr-2 text-orange-400"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Loading MO…
            </div>
          )}

          {moError && (
            <div className="flex-1 flex items-center justify-center text-red-400 text-sm px-6 text-center">
              ⚠ {moError}
            </div>
          )}

          {!moLoading && !moError && !mo && !bill?.relatedMO3 && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-600 gap-2">
              <svg
                className="w-10 h-10 opacity-40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="text-sm">No Material Order linked</p>
            </div>
          )}

          {!moLoading && !moError && mo && (
            <div className="flex-1 overflow-y-auto">
              {/* ── Comparison panel ── */}
              {showComparison && (
                <div className="border-b border-gray-800 bg-[#111827] px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Field Comparison
                    </span>
                    {hasMismatch && (
                      <span className="text-[10px] text-red-400">
                        ● mismatch
                      </span>
                    )}
                    {!hasMismatch && (
                      <span className="text-[10px] text-emerald-400">
                        ✓ all match
                      </span>
                    )}
                  </div>
                  {/* Column headers */}
                  <div className="grid grid-cols-[130px_1fr_1fr] gap-2 px-2 mb-1">
                    <span className="text-[10px] text-gray-600 uppercase">
                      Field
                    </span>
                    <span className="text-[10px] text-blue-400 uppercase font-semibold">
                      Bill
                    </span>
                    <span className="text-[10px] text-orange-400 uppercase font-semibold">
                      MO
                    </span>
                  </div>
                  <CmpRow
                    label="Vendor"
                    billVal={billVendor}
                    moVal={moVendor}
                  />
                  <CmpRow label="PO #" billVal={billPoNum} moVal={moPoNum} />

                  {/* Invoice subtotal (raw) */}
                  <CmpRow
                    label="Invoice Subtotal"
                    billVal={bill?.subtotal}
                    moVal={null}
                    money
                  />

                  {/* Credits row — only shown when credits exist */}
                  {creditTotal > 0 && (
                    <div className="grid grid-cols-[130px_1fr_1fr] gap-2 py-1.5 px-2 rounded text-xs bg-amber-950/25 border border-amber-800/30">
                      <span className="text-amber-500 font-medium">
                        Credits
                      </span>
                      <span className="text-amber-400 font-mono tabular-nums">
                        −${fmt(creditTotal)}
                      </span>
                      <span className="text-amber-600 text-[10px] italic">
                        pushed to MO
                      </span>
                    </div>
                  )}

                  {/* Effective amount vs MO total — this is what must match */}
                  <CmpRow
                    label={
                      creditTotal > 0 ? "Effective vs MO" : "Subtotal vs MO"
                    }
                    billVal={effectiveBillAmount}
                    moVal={moTotal}
                    money
                  />

                  {/* Tax (bill only) */}
                  <div className="grid grid-cols-[130px_1fr_1fr] gap-2 py-1.5 px-2 rounded text-xs odd:bg-gray-800/30">
                    <span className="text-gray-500 font-medium">
                      Tax (bill only)
                    </span>
                    <span className="text-gray-400 font-mono">
                      ${fmt(bill?.tax ?? 0)}
                    </span>
                    <span className="text-gray-600 text-[10px] italic">
                      not on MO
                    </span>
                  </div>

                  {/* MO Status row (keep as-is) */}
                  <div
                    className={`grid grid-cols-[130px_1fr_1fr] gap-2 py-1.5 px-2 rounded text-xs ${
                      mo?.status !== "delivered"
                        ? "bg-red-950/30 border border-red-800/40"
                        : "odd:bg-gray-800/30"
                    }`}
                  >
                    <span className="text-gray-500 font-medium">MO Status</span>
                    <span className="text-gray-600 italic text-[10px]">—</span>
                    <span
                      className={
                        mo?.status === "delivered"
                          ? "text-emerald-400 font-semibold"
                          : "text-red-300 font-semibold"
                      }
                    >
                      {mo?.status?.toUpperCase() || "—"}
                    </span>
                  </div>
                </div>
              )}

              {/* ── MO Overview ── */}
              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  {[
                    { label: "Order #", val: mo.orderNumber },
                    {
                      label: "Status",
                      val: <StatusPill status={mo.status} map={MO_STATUS} />,
                    },
                    {
                      label: "Vendor",
                      val:
                        typeof mo.vendor === "object"
                          ? mo.vendor?.companyName
                          : "—",
                    },
                    { label: "PO #", val: mo.purchaseOrder?.poNumber || "—" },
                    {
                      label: "Delivery",
                      val: mo.expectedDeliveryDate
                        ? new Date(mo.expectedDeliveryDate).toLocaleDateString(
                            "en-US",
                            { timeZone: "UTC" },
                          )
                        : "Not set",
                    },
                    { label: "Shipping", val: mo.shippingMethod || "—" },
                  ].map(({ label, val }) => (
                    <div key={label}>
                      <p className="text-gray-600 uppercase tracking-wide text-[9px] mb-0.5">
                        {label}
                      </p>
                      <div className="text-gray-200 font-medium">{val}</div>
                    </div>
                  ))}
                </div>

                {mo.deliveryInstructions && (
                  <div className="bg-gray-800/40 rounded-lg px-3 py-2 text-xs text-gray-400">
                    <span className="text-gray-600 text-[9px] uppercase tracking-wide block mb-0.5">
                      Delivery Instructions
                    </span>
                    {mo.deliveryInstructions}
                  </div>
                )}
              </div>

              {/* ── Line items ── */}
              <div className="px-4 pb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                    Line Items ({moItems.length})
                  </span>
                  <span className="text-[10px] text-orange-400 font-mono font-semibold">
                    Total: ${fmt(moTotal)}
                  </span>
                </div>

                <div className="rounded-lg border border-gray-800 overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-900/80">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-gray-500 font-medium">
                          Description
                        </th>
                        <th className="px-2 py-1.5 text-center text-gray-500 font-medium">
                          UOM
                        </th>
                        <th className="px-2 py-1.5 text-right text-gray-500 font-medium">
                          Qty
                        </th>
                        <th className="px-2 py-1.5 text-right text-gray-500 font-medium">
                          Delivered
                        </th>
                        <th className="px-2 py-1.5 text-right text-gray-500 font-medium">
                          Unit $
                        </th>
                        <th className="px-2 py-1.5 text-right text-gray-500 font-medium">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {moItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-2 py-4 text-center text-gray-600"
                          >
                            No items
                          </td>
                        </tr>
                      ) : (
                        moItems.map((item, i) => {
                          const isCov = isCoverageBased(item.uom);
                          const fullyDelivered =
                            (item.qtyDelivered || 0) >= (item.quantity || 0);
                          return (
                            <tr
                              key={item._id || i}
                              className={`${i % 2 === 0 ? "bg-gray-900/40" : "bg-gray-800/20"} ${
                                !fullyDelivered
                                  ? "border-l-2 border-amber-500/50"
                                  : ""
                              }`}
                            >
                              <td className="px-2 py-1.5 text-gray-200">
                                <div className="font-medium">
                                  {item.description || "—"}
                                </div>
                                {item.size && (
                                  <div className="text-[9px] text-gray-500 mt-0.5">
                                    {item.size}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-center text-gray-400">
                                {isCov ? "PC" : item.uom || "—"}
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-300 font-mono">
                                {item.quantity || 0}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">
                                <span
                                  className={
                                    fullyDelivered
                                      ? "text-emerald-400"
                                      : "text-amber-400"
                                  }
                                >
                                  {item.qtyDelivered || 0}
                                </span>
                                {!fullyDelivered && (
                                  <span className="text-gray-600 text-[9px] block">
                                    {item.qtyPending || 0} pending
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-300 font-mono">
                                ${fmt(item.unitPrice)}
                              </td>
                              <td className="px-2 py-1.5 text-right text-blue-300 font-mono font-semibold">
                                ${fmt(item.total)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {/* Surcharges */}
                    {(mo.surcharges || []).length > 0 && (
                      <>
                        <tbody className="divide-y divide-gray-800/30">
                          {mo.surcharges.map((sc, i) => (
                            <tr key={i} className="bg-gray-800/10">
                              <td
                                className="px-2 py-1.5 text-gray-500 italic"
                                colSpan={4}
                              >
                                {sc.description || "Surcharge"}
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-400 font-mono">
                                ${fmt(parseFloat(sc.price || sc.amount || 0))}
                              </td>
                              <td className="px-2 py-1.5 text-right text-blue-300 font-mono">
                                ${fmt(parseFloat(sc.price || sc.amount || 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    )}
                    <tfoot className="bg-gray-900 border-t border-gray-700">
                      <tr>
                        <td
                          colSpan={5}
                          className="px-2 py-2 text-right text-xs text-gray-400 font-semibold"
                        >
                          MO Total
                        </td>
                        <td className="px-2 py-2 text-right text-sm font-bold text-orange-400 font-mono">
                          ${fmt(moTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* ── MO Attachments (sales orders, packing slips) ── */}
              {(mo.attachments || mo.files || []).length > 0 && (
                <div className="px-4 pb-4 pt-1">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    MO Attachments
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(mo.attachments || mo.files || []).map((a, i) => (
                      <a
                        key={i}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-[10px] rounded bg-gray-800 border border-gray-700 text-blue-400 hover:text-blue-300 hover:border-blue-600 transition-colors truncate max-w-[140px]"
                        title={a.name}
                      >
                        📎 {a.name || `File ${i + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Bill financials summary ── */}
              <div className="mx-4 mb-4 mt-1 rounded-xl bg-gray-800/40 border border-gray-700/50 overflow-hidden">
                <div className="px-3 py-2 bg-gray-900/60 border-b border-gray-800">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Bill Summary
                  </span>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  {[
                    { label: "Invoice #", val: bill?.invoiceNumber || "—" },
                    {
                      label: "Invoice Date",
                      val: bill?.invoiceDate
                        ? new Date(bill.invoiceDate).toLocaleDateString()
                        : "—",
                    },
                    {
                      label: "Due Date",
                      val: bill?.dueDate
                        ? new Date(bill.dueDate).toLocaleDateString()
                        : "—",
                    },
                    {
                      label: "Subtotal",
                      val: `$${fmt(bill?.subtotal)}`,
                      mono: true,
                    },
                    { label: "Tax", val: `$${fmt(bill?.tax)}`, mono: true },
                    {
                      label: "Total",
                      val: `$${fmt(bill?.total)}`,
                      mono: true,
                      highlight: true,
                    },
                  ].map(({ label, val, mono, highlight }) => (
                    <div
                      key={label}
                      className="flex justify-between items-center text-xs"
                    >
                      <span className="text-gray-500">{label}</span>
                      <span
                        className={`${mono ? "font-mono" : ""} ${highlight ? "text-orange-400 font-bold text-sm" : "text-gray-200"}`}
                      >
                        {val}
                      </span>
                    </div>
                  ))}

                  {/* Credits — shown only if bill has any */}
                  {(bill?.credits || []).length > 0 && (
                    <>
                      <div className="border-t border-amber-800/30 pt-1.5 mt-1 space-y-1">
                        {(bill.credits || []).map((c, i) => (
                          <div
                            key={i}
                            className="flex justify-between items-center text-xs"
                          >
                            <span
                              className="text-amber-500 truncate max-w-[120px]"
                              title={c.description}
                            >
                              Credit {i + 1}
                              {c.description ? `: ${c.description}` : ""}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {c.attachment?.url && (
                                <a
                                  href={c.attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[10px] text-blue-400 hover:underline"
                                >
                                  📄
                                </a>
                              )}
                              <span className="font-mono text-amber-400">
                                −${fmt(c.amount || 0)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center text-xs border-t border-amber-800/30 pt-1.5 mt-0.5">
                        <span className="text-emerald-400 font-semibold">
                          Effective Amount
                        </span>
                        <span
                          className={`font-mono font-bold text-sm ${
                            subtotalMatchesMO
                              ? "text-emerald-400"
                              : "text-red-400"
                          }`}
                        >
                          ${fmt(effectiveBillAmount)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Undelivered warning */}
              {mo.status !== "delivered" && mo.status !== "void" && (
                <div className="mx-4 mb-4 px-3 py-2.5 rounded-lg bg-amber-950/30 border border-amber-700/40 text-xs text-amber-300 flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">⚠</span>
                  <span>
                    MO status is <strong>{mo.status?.toUpperCase()}</strong> —
                    not fully delivered. Confirm this bill only after all items
                    are received.
                  </span>
                </div>
              )}

              {/* ── Notes ── */}
              {bill?.notes && (
                <div className="mx-4 mb-4 px-3 py-2.5 rounded-lg bg-gray-800/40 border border-gray-700/40 text-xs text-gray-400">
                  <span className="text-[9px] text-gray-600 uppercase tracking-wide block mb-1">
                    Notes
                  </span>
                  {bill.notes}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
